import React, { useState, useEffect } from 'react';
import { Calendar, User, TrendingUp, AlertCircle, DollarSign, Users, Clock, FileText } from 'lucide-react';

const SystemReportsTab = ({ isDark = false, selectedPeriod = 'month' }) => {
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [filters, setFilters] = useState({
    officer: 'all',
    dateFrom: '',
    dateTo: '',
    status: 'all'
  });
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMeetingData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, meetings]);

  const fetchMeetingData = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await fetch('/api/system-reports/meetings');
      // const data = await response.json();
      
      // Mock data for demonstration
      const mockData = [
        {
          id: 1,
          groupName: "Jitegemee Group",
          meetingDate: "2025-11-15",
          officer: "John Kamau",
          membersContributed: 12,
          totalMembers: 15,
          loansRepaid: 45000,
          totalSavings: 25000,
          loansGiven: 80000,
          advancesGiven: 15000,
          status: "closed",
          lateContributors: ["Mary Wanjiru", "Peter Omondi"],
          nonRepayingLoanMembers: ["Jane Achieng"],
          nonRepayingAdvanceMembers: []
        },
        {
          id: 2,
          groupName: "Upendo Welfare",
          meetingDate: "2025-11-14",
          officer: "Sarah Njeri",
          membersContributed: 20,
          totalMembers: 20,
          loansRepaid: 120000,
          totalSavings: 60000,
          loansGiven: 150000,
          advancesGiven: 0,
          status: "active",
          lateContributors: [],
          nonRepayingLoanMembers: ["James Mwangi", "Grace Akinyi"],
          nonRepayingAdvanceMembers: []
        },
        {
          id: 3,
          groupName: "Tumaini Sacco",
          meetingDate: "2025-11-13",
          officer: "John Kamau",
          membersContributed: 8,
          totalMembers: 10,
          loansRepaid: 30000,
          totalSavings: 15000,
          loansGiven: 50000,
          advancesGiven: 8000,
          status: "open",
          lateContributors: ["Alice Wambui"],
          nonRepayingLoanMembers: [],
          nonRepayingAdvanceMembers: ["Tom Odhiambo"]
        }
      ];

      setMeetings(mockData);
      
      // Extract unique officers
      const uniqueOfficers = [...new Set(mockData.map(m => m.officer))];
      setOfficers(uniqueOfficers);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching meeting data:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...meetings];

    if (filters.officer !== 'all') {
      filtered = filtered.filter(m => m.officer === filters.officer);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(m => m.meetingDate >= filters.dateFrom);
    }

    if (filters.dateTo) {
      filtered = filtered.filter(m => m.meetingDate <= filters.dateTo);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(m => m.status === filters.status);
    }

    setFilteredMeetings(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-800 border-blue-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.open}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading meeting reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          System Reports
        </h2>
        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          Comprehensive meeting reports with member contributions, loan repayments, and performance metrics
        </p>
      </div>

      {/* Filters */}
      <div className={`p-6 rounded-lg ${isDark ? 'bg-[#2b323c]' : 'bg-white'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Filter Reports
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Attended By
            </label>
            <select
              value={filters.officer}
              onChange={(e) => setFilters({ ...filters, officer: e.target.value })}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-[#1f262e] border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="all">All Officers</option>
              {officers.map(officer => (
                <option key={officer} value={officer}>{officer}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Date From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-[#1f262e] border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Date To
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-[#1f262e] border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-[#1f262e] border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Meeting Reports */}
      <div className="space-y-4">
        {filteredMeetings.length === 0 ? (
          <div className={`p-8 rounded-lg text-center ${isDark ? 'bg-[#2b323c]' : 'bg-white'}`}>
            <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              No meeting reports match the selected filters
            </p>
          </div>
        ) : (
          filteredMeetings.map(meeting => (
            <div
              key={meeting.id}
              className={`p-6 rounded-lg border ${
                isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              {/* Meeting Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {meeting.groupName}
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {formatDate(meeting.meetingDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {meeting.officer}
                      </span>
                    </div>
                  </div>
                </div>
                {getStatusBadge(meeting.status)}
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Members Contributed
                    </span>
                  </div>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {meeting.membersContributed}/{meeting.totalMembers}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {((meeting.membersContributed / meeting.totalMembers) * 100).toFixed(0)}% participation rate
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Total Savings
                    </span>
                  </div>
                  <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(meeting.totalSavings)}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Collected during meeting
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Loans Repaid
                    </span>
                  </div>
                  <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(meeting.loansRepaid)}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Total repayments received
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className={`w-4 h-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Loans Disbursed
                    </span>
                  </div>
                  <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(meeting.loansGiven)}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    New loans issued
                  </p>
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-4">
                {meeting.advancesGiven > 0 && (
                  <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Advances Given
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Total advances disbursed during this meeting: <span className="font-bold">{formatCurrency(meeting.advancesGiven)}</span>
                    </p>
                  </div>
                )}

                {meeting.lateContributors.length > 0 && (
                  <div className={`p-4 rounded-lg border-l-4 border-yellow-500 ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
                    <div className="flex items-start gap-2">
                      <Clock className={`w-5 h-5 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                      <div>
                        <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Late Contributors ({meeting.lateContributors.length})
                        </h4>
                        <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          These members made their contributions late, requiring follow-up by the officer:
                        </p>
                        <ul className={`text-sm list-disc list-inside ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {meeting.lateContributors.map((member, idx) => (
                            <li key={idx}>{member}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {meeting.nonRepayingLoanMembers.length > 0 && (
                  <div className={`p-4 rounded-lg border-l-4 border-red-500 ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-5 h-5 mt-0.5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                      <div>
                        <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Non-Repaying Loan Members ({meeting.nonRepayingLoanMembers.length})
                        </h4>
                        <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Members with outstanding loan balances who failed to make any repayment:
                        </p>
                        <ul className={`text-sm list-disc list-inside ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {meeting.nonRepayingLoanMembers.map((member, idx) => (
                            <li key={idx}>{member}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {meeting.nonRepayingAdvanceMembers.length > 0 && (
                  <div className={`p-4 rounded-lg border-l-4 border-orange-500 ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-5 h-5 mt-0.5 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                      <div>
                        <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Non-Repaying Advance Members ({meeting.nonRepayingAdvanceMembers.length})
                        </h4>
                        <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Members with outstanding advance balances who failed to make any repayment:
                        </p>
                        <ul className={`text-sm list-disc list-inside ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {meeting.nonRepayingAdvanceMembers.map((member, idx) => (
                            <li key={idx}>{member}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SystemReportsTab;